import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { ShoppingCart, Truck, Store, Plus, Trash2, Loader2, CheckCircle2, ScanLine, X, Search, ListChecks } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { ORDER_SOURCES, orderSourceAttribution } from '@/lib/orderSources';
import { getRxkSkus } from '@/lib/rxkSkus';
import { useUserRoleContext } from '@/contexts/UserRoleContext';
import { sortProducts } from '@/lib/productOrder';

interface PartnerOption {
  id: string;
  name: string;
  type: string;
  status: string;
  advance_payment_pct: number;
  first_order_advance_pct: number;
  wholesale_discount_pct: number | null;
  phone: string | null;
  address: string | null;
}

interface ProductOption {
  sku: string;
  name_en: string | null;
  msrp_bdt: number | null;
  // The prefix real inventory_codes actually carry (e.g. "RXB" for
  // RXK-BST50's "RXB-000003") — assigned independently of the SKU string,
  // so it can NEVER be derived by stripping the SKU itself.
  serial_prefix: string | null;
  gift_box_free: boolean;
}

interface OrderLine {
  sku: string;
  qty: string;
  // Inventory_codes scanned in for this line — when present, this line's
  // stock deduction is tied to those exact physical units (they get
  // marked 'sold' + linked to the order right after it's created), not
  // just a generic SKU/qty deduction. For a bundle line these mix codes
  // from more than one component sku — grouped back out per-component by
  // prefix wherever they're displayed.
  scannedCodes: string[];
}

interface BundlePart {
  sku: string;
  qty_per_bundle: number;
}

// A small self-contained combobox: typing shows matching in-stock serials
// right under the field (no native <datalist>, which renders as an
// unstyled, often huge browser popup) — click one or press Enter to
// submit whatever's typed.
//
// `suggestions` is the fast local list (shown instantly, filtered
// client-side) — but every caller's list is itself capped at .limit(300)
// per SKU for fetch performance, so a real code past that first page would
// never show a suggestion at all. `liveSearch`, when given, runs a
// debounced real query and — once it resolves — replaces the local-filtered
// list so a match well beyond the cap still shows up.
function SerialTypeahead({
  value,
  onChange,
  onSubmit,
  suggestions,
  liveSearch,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (code: string) => void;
  suggestions: string[];
  liveSearch?: (query: string) => Promise<string[]>;
  placeholder: string;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [liveMatches, setLiveMatches] = useState<string[] | null>(null);
  const trimmed = value.trim();

  useEffect(() => {
    // Clear immediately on every keystroke — otherwise the PREVIOUS
    // keystroke's live results (a different query) linger on screen until
    // the new debounce resolves, then can suddenly flip to empty/hidden
    // once it does. Falling back to the local `suggestions` filter in the
    // meantime keeps something reasonable on screen instead of a blank gap.
    setLiveMatches(null);
    if (!liveSearch || !trimmed) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const results = await liveSearch(trimmed);
      if (!cancelled) setLiveMatches(results);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [liveSearch, trimmed]);

  const matches = trimmed ? (liveMatches ?? suggestions.filter((c) => c.includes(trimmed))).slice(0, 6) : [];
  return (
    <div className="relative flex-1">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onKeyDown={(e) => {
          // Was a nested <form onSubmit>, wrapping an Input inside the New
          // Order modal's own outer <form> — invalid HTML (a form can't
          // contain a form), which the browser silently "fixes" by
          // restructuring the DOM. That broke this inner form's own
          // onSubmit from reliably catching Enter, so a real barcode
          // scanner (which always sends Enter after the code) could instead
          // trigger the OUTER form's native submit — an uncontrolled page
          // navigation that discarded the entire in-progress order. Handling
          // Enter directly on the input, with no <form> at all, keeps this
          // fully contained regardless of what it's nested inside.
          if (e.key === 'Enter') {
            e.preventDefault();
            if (value.trim()) onSubmit(value.trim());
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className="font-mono text-[11px] !py-1.5"
      />
      {focused && matches.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-border-subtle shadow-modal max-h-40 overflow-y-auto">
          {matches.map((code) => (
            <button
              key={code}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSubmit(code);
                onChange('');
              }}
              className="w-full text-left px-2.5 py-1.5 text-[11px] font-mono hover:bg-primary-50 cursor-pointer"
            >
              {code}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface PaymentGateway {
  gateway_key: string;
  display_name: string;
  display_order: number;
}

// Fallback only, in case core_shipping_rates_public_v hasn't loaded yet —
// the real rates come from ERP > Delivery Merchants (core_shipping_rates),
// same source the public checkout and core-place-order use.
const FALLBACK_SHIPPING_RATES = { dhaka: 60, outside: 120, freeThreshold: 3000 };

const EMPTY_FORM = {
  payment_method: 'cod',
  customer_name: '',
  customer_phone: '',
  customer_address: '',
  discount_bdt: '',
  shipping_bdt: String(FALLBACK_SHIPPING_RATES.dhaka),
  is_gift: false,
  gift_reason: '',
  came_from: '',
};

// Shared "New Order" modal — used by both the main Orders page and the
// Reseller > Orders tab (with defaultReseller pre-checking the reseller
// picker there) so staff never have to leave the reseller context just to
// place an order on a partner's behalf.
export function NewOrderModal({
  open,
  onClose,
  onCreated,
  defaultReseller = false,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultReseller?: boolean;
}) {
  const { push } = useToast();
  const { role } = useUserRoleContext();
  const isPriv = role === 'admin' || role === 'admin_low';
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [stockBySku, setStockBySku] = useState<Record<string, number>>({});
  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway[]>([]);
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [createBusy, setCreateBusy] = useState(false);
  const [fulfillment, setFulfillment] = useState<'delivery' | 'instore'>('delivery');
  // Mirrors the public checkout's two-zone courier rate (৳60 inside Dhaka,
  // ৳120 outside) so staff pick a zone instead of having to remember/type
  // the right number — the amount field stays editable below for the rare
  // manual override (remote area surcharge, negotiated rate, etc).
  const [deliveryZone, setDeliveryZone] = useState<'dhaka' | 'outside'>('dhaka');
  const [shippingRates, setShippingRates] = useState(FALLBACK_SHIPPING_RATES);
  const [isReseller, setIsReseller] = useState(defaultReseller);
  const [partnerId, setPartnerId] = useState('');
  const [discountPct, setDiscountPct] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [lines, setLines] = useState<OrderLine[]>([{ sku: '', qty: '1', scannedCodes: [] }]);
  const [phoneLookupBusy, setPhoneLookupBusy] = useState(false);
  const [phoneMatched, setPhoneMatched] = useState<'account' | 'previous' | null>(null);
  const [scanning, setScanning] = useState(false);
  const [availableCodes, setAvailableCodes] = useState<string[]>([]);
  const [bundleComponents, setBundleComponents] = useState<Record<string, BundlePart[]>>({});
  const [giftBoxPrice, setGiftBoxPrice] = useState<number | null>(null);
  // Manual, standalone gift box quantity (no combo on the order) — how many
  // to add, not just whether to. Ignored while hasCombo is true, since a
  // combo order's box count is driven by its own set quantity instead.
  const [giftBoxQty, setGiftBoxQty] = useState('0');
  // Admin/admin_low only — waives the charge for every standalone box on
  // this order (all-or-nothing, not per-unit). Same trust boundary as the
  // reseller discount override: core_create_order only honors a sent
  // unit_price_bdt for privileged roles, so bd_staff never sees this toggle
  // — it would silently have no effect for them.
  const [giftBoxFree, setGiftBoxFree] = useState(false);
  // Optional — the physical box's own printed serial (receiving already
  // generates + prints one per unit; scanning it here at sale time is what
  // actually marks that serial 'sold', same as any other product's line).
  // Never required: box stock/order-creation must never block on this. One
  // per set — a 10-set combo order needs up to 10 distinct box serials,
  // same "one code per physical unit" rule as any other line's scannedCodes.
  const [giftBoxSerials, setGiftBoxSerials] = useState<string[]>([]);
  const [giftBoxCodes, setGiftBoxCodes] = useState<string[]>([]);
  const [serialDrafts, setSerialDrafts] = useState<Record<string, string>>({});
  const [pickerLineIdx, setPickerLineIdx] = useState<number | 'giftbox' | null>(null);
  const [pickerComponentSku, setPickerComponentSku] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  // null = no active search (fall back to the capped prefetch below);
  // populated by a live DB query once the user types — the prefetch
  // (availableCodes / giftBoxCodes) only ever holds the first 300 codes
  // per SKU, so filtering it client-side can never find a real code that
  // just didn't happen to land in that first page.
  const [pickerLiveMatches, setPickerLiveMatches] = useState<string[] | null>(null);
  const scanBufferRef = useRef('');
  const lastScanKeyRef = useRef(0);
  const handleScanBarcodeRef = useRef<(code: string) => void>(() => {});

  useEffect(() => {
    supabase
      .from('core_products')
      .select('sku, name_en, msrp_bdt, serial_prefix, gift_box_free')
      .eq('brand', 'rxk')
      .eq('status', 'active')
      .order('sku')
      .then(({ data }) => setProducts(sortProducts(data as ProductOption[])));
    getRxkSkus().then((RXK_SKUS) => Promise.all([
      // GIFT-BOX is in RXK_SKUS so stockBySku can tell whether there's
      // actually a box on hand right now — the combo order's free gift box
      // shouldn't claim to be included when there are none, even if
      // core_create_order will silently skip it anyway.
      supabase.from('core_stock_current_v').select('sku, qty_on_hand').in('sku', RXK_SKUS),
      supabase.from('core_bundle_stock_v').select('bundle_sku, available_sets'),
    ])).then(([stockRes, bundleRes]) => {
      const totals: Record<string, number> = {};
      for (const row of (stockRes.data as { sku: string; qty_on_hand: number }[]) ?? []) {
        totals[row.sku] = (totals[row.sku] ?? 0) + Number(row.qty_on_hand);
      }
      // Bundle SKUs (e.g. SET-3STEP) have no stock ledger of their own — a
      // "set" is assembled from its components at fulfillment — so their
      // available quantity is however many complete sets those components
      // can still make, not whatever core_stock_current_v says (always 0).
      for (const row of (bundleRes.data as { bundle_sku: string; available_sets: number }[]) ?? []) {
        totals[row.bundle_sku] = row.available_sets;
      }
      setStockBySku(totals);
    });
    supabase
      .from('core_product_bundles')
      .select('bundle_sku, component_sku, qty_per_bundle')
      .then(({ data }) => {
        const map: Record<string, BundlePart[]> = {};
        for (const row of (data as { bundle_sku: string; component_sku: string; qty_per_bundle: number }[]) ?? []) {
          (map[row.bundle_sku] ??= []).push({ sku: row.component_sku, qty_per_bundle: row.qty_per_bundle });
        }
        // Set parts in routine order (Booster, Serum, Cream), not DB order.
        for (const k of Object.keys(map)) map[k] = sortProducts(map[k]);
        setBundleComponents(map);
      });
    supabase
      .from('core_products')
      .select('msrp_bdt')
      .eq('sku', 'GIFT-BOX')
      .eq('status', 'active')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.msrp_bdt != null) setGiftBoxPrice(Number(data.msrp_bdt));
      });
    supabase
      .from('core_payment_gateways')
      .select('gateway_key, display_name, display_order')
      .eq('is_enabled', true)
      .order('display_order')
      .then(({ data }) => setPaymentGateways((data as PaymentGateway[]) ?? []));
    supabase
      .from('core_partners')
      .select('id, name, type, status, advance_payment_pct, first_order_advance_pct, wholesale_discount_pct, phone, address')
      .order('name')
      .then(({ data }) => setPartners((data as PartnerOption[]) ?? []));
    supabase
      .from('core_shipping_rates_public_v')
      .select('dhaka_charge_bdt, outside_dhaka_charge_bdt, free_shipping_threshold_bdt')
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const rates = {
          dhaka: Number(data.dhaka_charge_bdt),
          outside: Number(data.outside_dhaka_charge_bdt),
          freeThreshold: Number(data.free_shipping_threshold_bdt),
        };
        setShippingRates(rates);
        setForm((f) => ({ ...f, shipping_bdt: String(rates.dhaka) }));
      });
  }, []);

  // First-ever pharmacy_b2b order for a partner always ships on 100%
  // credit (consignment) — mirrors the same count check core_create_order
  // does server-side, so staff sees the real due-now/on-credit split before
  // submitting instead of only after the order lands.
  const [priorOrderCount, setPriorOrderCount] = useState<number | null>(null);
  useEffect(() => {
    if (!isReseller || !partnerId) {
      setPriorOrderCount(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('core_orders')
      .select('id', { count: 'exact', head: true })
      .eq('partner_id', partnerId)
      .eq('channel', 'pharmacy_b2b')
      .neq('status', 'cancelled')
      .then(({ count }) => {
        if (!cancelled) setPriorOrderCount(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [isReseller, partnerId]);

  // Live lookup as the phone number is typed: a registered rxk_customers
  // account first, else the most recent guest order that used this number.
  useEffect(() => {
    const phone = form.customer_phone.trim();
    if (phone.length < 7) {
      setPhoneMatched(null);
      return;
    }
    let cancelled = false;
    setPhoneLookupBusy(true);
    const t = setTimeout(async () => {
      const { data: account } = await supabase
        .from('rxk_customers')
        .select('full_name, address_line1, address_line2, area, city, postal_code')
        .eq('phone', phone)
        .maybeSingle();
      if (cancelled) return;
      if (account) {
        const address = [account.address_line1, account.address_line2, account.area, account.city, account.postal_code].filter(Boolean).join(', ');
        setForm((f) => ({ ...f, customer_name: account.full_name, customer_address: address || f.customer_address }));
        setPhoneMatched('account');
        setPhoneLookupBusy(false);
        return;
      }

      const { data: prevOrder } = await supabase
        .from('core_orders')
        .select('customer_info')
        .eq('customer_info->>phone', phone)
        .order('placed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const info = prevOrder?.customer_info as { name?: string; address?: string } | undefined;
      if (info?.name) {
        setForm((f) => ({ ...f, customer_name: info.name ?? f.customer_name, customer_address: info.address || f.customer_address }));
        setPhoneMatched('previous');
      } else {
        setPhoneMatched(null);
      }
      setPhoneLookupBusy(false);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.customer_phone]);

  // Not every till has a barcode scanner attached — this backs the manual
  // entry field below with a suggestion list of what's actually in stock
  // right now, so staff can either type/paste a serial or just pick one.
  // Queried per-SKU (rather than one global `order by inventory_code limit
  // 500` across every product) because that single cap let whichever SKU
  // sorts first alphabetically consume the entire budget — e.g. RXK-BST50
  // alone has 4,000+ assigned codes, so a global top-500 came back 100%
  // Booster and left every other product's picker showing "0 in-stock
  // serials available" even though thousands of their own codes exist.
  useEffect(() => {
    if (!open) return;
    getRxkSkus().then((RXK_SKUS) => Promise.all(
      RXK_SKUS.map((sku) =>
        supabase
          .from('core_product_authenticity_codes')
          .select('inventory_code')
          .eq('status', 'assigned')
          .eq('sku', sku)
          .order('inventory_code')
          .limit(300),
      ),
    )).then((results) => {
      const codes = results.flatMap((r) => ((r.data as { inventory_code: string }[]) ?? []).map((row) => row.inventory_code));
      setAvailableCodes(codes);
    });
    // Kept as its own fetch into a separate `giftBoxCodes` state rather than
    // just reading GIFT-BOX's slice out of `availableCodes` above — GIFT-BOX
    // never actually has assigned codes (requires_serial=false, received as
    // a plain quantity), but the picker below needs a dedicated state either
    // way since it's presented as a distinct standalone product, not one of
    // the per-step lines RXK_SKUS otherwise builds.
    supabase
      .from('core_product_authenticity_codes')
      .select('inventory_code')
      .eq('status', 'assigned')
      .eq('sku', 'GIFT-BOX')
      .order('inventory_code')
      .limit(300)
      .then(({ data }) => setGiftBoxCodes(((data as { inventory_code: string }[]) ?? []).map((row) => row.inventory_code)));
  }, [open]);

  const addLine = () => setLines((prev) => [...prev, { sku: '', qty: '1', scannedCodes: [] }]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));
  const updateLine = (idx: number, patch: Partial<OrderLine>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  // Scanning a unit's barcode (inventory_code) adds/increments its SKU's
  // line item and pulls in the price from the product list automatically
  // — each scan represents one physical unit, so scanning the same
  // product again just bumps that line's qty instead of adding a
  // duplicate row. There's no dedicated on-screen field to scan into —
  // the global keydown listener below feeds this directly, so aiming the
  // scanner anywhere on the form (while no text field is focused) just
  // works.
  const handleScanBarcode = async (rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) return;
    // Aiming a scanner at the screen repeatedly re-fires the same code —
    // unlike addSerialToLine/addGiftBoxSerial (both already guard this),
    // this global anywhere-on-the-page path had no such check, so the same
    // physical unit could be added over and over just from an accidental
    // double-scan. A code is only ever valid on one line at a time, so
    // checking across every line (not just whichever one it'll land on) is
    // correct here.
    if (lines.some((l) => l.scannedCodes.includes(code))) {
      push('error', 'That serial is already added to this order');
      return;
    }
    setScanning(true);
    const { data, error } = await supabase
      .from('core_product_authenticity_codes')
      .select('sku, status')
      .eq('inventory_code', code)
      .maybeSingle();
    setScanning(false);
    if (error || !data) {
      push('error', 'Barcode not found');
      return;
    }
    if (data.status === 'sold') {
      push('error', 'This unit was already sold');
      return;
    }
    if (data.status === 'unused') {
      push('error', "This code hasn't been received into inventory yet");
      return;
    }
    const product = products.find((p) => p.sku === data.sku);
    if (!product) {
      push('error', `Scanned product (${data.sku}) isn't available for sale`);
      return;
    }
    // Bundle components are capped at however many sets are actually on the
    // order (qty_per_bundle * that line's qty) — same cap the per-line
    // field already enforces by hiding itself once reached; the global
    // scan path bypassed that entirely since it never read `needed`.
    const bundleLine = lines.find((l) => bundleComponents[l.sku]?.some((c) => c.sku === data.sku));
    if (bundleLine) {
      const part = bundleComponents[bundleLine.sku]!.find((c) => c.sku === data.sku)!;
      const needed = (Number(bundleLine.qty) || 0) * part.qty_per_bundle;
      const partPrefix = (product.serial_prefix ?? '').toUpperCase();
      const alreadyForPart = bundleLine.scannedCodes.filter((c) => c.startsWith(partPrefix)).length;
      if (alreadyForPart >= needed) {
        push('error', `Already have ${needed} ${product.name_en || data.sku} serial${needed === 1 ? '' : 's'} — that's all this order needs`);
        return;
      }
    }
    setLines((prev) => {
      const existingIdx = prev.findIndex((l) => l.sku === data.sku);
      if (existingIdx >= 0) {
        return prev.map((l, i) =>
          i === existingIdx ? { ...l, qty: String(l.scannedCodes.length + 1), scannedCodes: [...l.scannedCodes, code] } : l,
        );
      }
      // Scanned unit is a component of a bundle line already on the
      // order (e.g. the Booster from a 3-Step Set) — goes into that
      // line's mixed scannedCodes, qty (number of sets) untouched.
      const bundleIdx = prev.findIndex((l) => bundleComponents[l.sku]?.some((c) => c.sku === data.sku));
      if (bundleIdx >= 0) {
        return prev.map((l, i) => (i === bundleIdx ? { ...l, scannedCodes: [...l.scannedCodes, code] } : l));
      }
      const emptyIdx = prev.findIndex((l) => !l.sku);
      if (emptyIdx >= 0) {
        return prev.map((l, i) => (i === emptyIdx ? { ...l, sku: data.sku, qty: '1', scannedCodes: [code] } : l));
      }
      return [...prev, { sku: data.sku, qty: '1', scannedCodes: [code] }];
    });
    push('success', `Added ${product.name_en || data.sku}`);
  };
  handleScanBarcodeRef.current = handleScanBarcode;

  // Backs SerialTypeahead's liveSearch — a real query against the table
  // (not the .limit(300)-per-SKU prefetch used for the instant local
  // dropdown), so typing/scanning a code well past that cap still finds a
  // match. Used identically by regular lines, bundle components, and Gift
  // Box below.
  const searchSerials = async (sku: string, query: string): Promise<string[]> => {
    if (!sku || !query) return [];
    const { data, error } = await supabase
      .from('core_product_authenticity_codes')
      .select('inventory_code')
      .eq('sku', sku)
      .eq('status', 'assigned')
      .ilike('inventory_code', `%${query}%`)
      .order('inventory_code')
      .limit(20);
    // A failed query and a genuine zero-match query both used to look
    // identical to the user (empty dropdown, no explanation) — this was
    // silently swallowing whatever went wrong instead of surfacing it.
    if (error) {
      console.error('[searchSerials] query failed:', error.message);
      return [];
    }
    return ((data as { inventory_code: string }[] | null) ?? []).map((r) => r.inventory_code);
  };

  // Adds a serial to one specific line (the per-line "Serial(s)" field) —
  // unlike handleScanBarcode (which finds/creates whichever line matches),
  // this targets an exact row, so a code belonging to a different product
  // than what's already picked there is rejected instead of silently
  // landing on the wrong line.
  const addSerialToLine = async (idx: number, rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) return;
    const line = lines[idx];
    if (line.scannedCodes.includes(code)) {
      push('error', 'That serial is already added to this line');
      return;
    }
    setScanning(true);
    const { data, error } = await supabase.from('core_product_authenticity_codes').select('sku, status').eq('inventory_code', code).maybeSingle();
    setScanning(false);
    if (error || !data) {
      push('error', 'Serial not found');
      return;
    }
    if (data.status === 'sold') {
      push('error', 'This unit was already sold');
      return;
    }
    if (data.status === 'unused') {
      push('error', "This code hasn't been received into inventory yet");
      return;
    }
    const bundleParts = bundleComponents[line.sku];
    const isComponentMatch = bundleParts?.some((c) => c.sku === data.sku);
    if (line.sku && data.sku !== line.sku && !isComponentMatch) {
      push('error', `This serial belongs to a different product (${data.sku})`);
      return;
    }
    const product = products.find((p) => p.sku === data.sku);
    if (!product) {
      push('error', `${data.sku} isn't available for sale`);
      return;
    }
    const nextCodes = [...line.scannedCodes, code];
    // A bundle line's qty means "how many sets" — unrelated to how many
    // component serials have been linked — so it's left alone here.
    if (bundleParts) {
      updateLine(idx, { scannedCodes: nextCodes });
    } else {
      updateLine(idx, { sku: data.sku, scannedCodes: nextCodes, qty: String(nextCodes.length) });
    }
  };

  // Same live, exact-code DB check as addSerialToLine above — not limited
  // to whatever's in giftBoxCodes (that list is capped at .limit(300) for
  // picker performance), so any real, in-stock box serial is accepted
  // whether or not it happened to be in that prefetched page.
  const addGiftBoxSerial = async (rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) return;
    if (giftBoxSerials.includes(code)) {
      push('error', 'That box is already scanned');
      return;
    }
    if (giftBoxSerials.length >= giftBoxNeeded) {
      push('error', `Already have ${giftBoxNeeded} box serial${giftBoxNeeded === 1 ? '' : 's'} — that's all this order needs`);
      return;
    }
    setScanning(true);
    const { data, error } = await supabase.from('core_product_authenticity_codes').select('sku, status').eq('inventory_code', code).maybeSingle();
    setScanning(false);
    if (error || !data) {
      push('error', 'Serial not found');
      return;
    }
    if (data.sku !== 'GIFT-BOX') {
      push('error', `This serial belongs to a different product (${data.sku})`);
      return;
    }
    if (data.status === 'sold') {
      push('error', 'This box was already sold');
      return;
    }
    if (data.status === 'unused') {
      push('error', "This code hasn't been received into inventory yet");
      return;
    }
    setGiftBoxSerials((prev) => [...prev, code]);
  };

  // Barcode scanners are just fast keyboards — this listens for a burst of
  // keystrokes terminated by Enter so aiming the scanner anywhere on the
  // open form adds the item, with no dedicated field to click into first.
  // It backs off while a real text field / dropdown is focused so normal
  // typing (customer name, address, manual product pick) is never touched.
  useEffect(() => {
    if (!open) return;
    const SCAN_KEY_GAP_MS = 50;
    const MIN_SCAN_LENGTH = 6;
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const now = Date.now();
      const gap = now - lastScanKeyRef.current;
      lastScanKeyRef.current = now;
      if (e.key === 'Enter') {
        const code = scanBufferRef.current;
        scanBufferRef.current = '';
        if (code.length >= MIN_SCAN_LENGTH) {
          e.preventDefault();
          handleScanBarcodeRef.current(code);
        }
        return;
      }
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
      if (gap > SCAN_KEY_GAP_MS) scanBufferRef.current = '';
      scanBufferRef.current += e.key;
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // For a bundle (SET-*) line, the "true" subtotal contribution is the sum
  // of its components' own MSRP, not the bundle SKU's own (discounted)
  // msrp_bdt — mirrors what core_create_order now computes server-side for
  // v_orig_subtotal, so the modal shows the same ৳8,500-style figure staff
  // will see on the printed invoice.
  const orderSubtotal = form.is_gift
    ? 0
    : lines.reduce((sum, l) => {
        const qty = Number(l.qty) || 0;
        const bundleParts = bundleComponents[l.sku];
        if (bundleParts && bundleParts.length > 0) {
          const componentsTotal = bundleParts.reduce(
            (s, part) => s + (products.find((p) => p.sku === part.sku)?.msrp_bdt ?? 0) * part.qty_per_bundle,
            0,
          );
          return sum + componentsTotal * qty;
        }
        const product = products.find((p) => p.sku === l.sku);
        return sum + (product?.msrp_bdt ?? 0) * qty;
      }, 0);

  // Automatic bundle-savings portion (component sum minus the bundle's own
  // price) — core_create_order applies this same amount server-side
  // unconditionally, so it's auto-filled into the Discount field here for
  // display/edit but only the amount ABOVE it is sent as discount_bdt on
  // submit (see handleSubmit) to avoid double-counting it.
  const autoBundleDiscount = form.is_gift
    ? 0
    : lines.reduce((sum, l) => {
        const qty = Number(l.qty) || 0;
        const bundleParts = bundleComponents[l.sku];
        if (!bundleParts || bundleParts.length === 0) return sum;
        const componentsTotal = bundleParts.reduce(
          (s, part) => s + (products.find((p) => p.sku === part.sku)?.msrp_bdt ?? 0) * part.qty_per_bundle,
          0,
        );
        const bundleOwnPrice = products.find((p) => p.sku === l.sku)?.msrp_bdt ?? 0;
        return sum + Math.max(0, componentsTotal - bundleOwnPrice) * qty;
      }, 0);

  const selectedPartner = partners.find((p) => p.id === partnerId);
  // This reseller's contracted wholesale discount, off each line's own MSRP
  // (mirrors core-place-reseller-order's exact formula: msrp * (1 - pct/100),
  // rounded to the nearest taka). 0 when not a reseller order, or the
  // partner has no discount configured — full MSRP applies as before.
  const resellerDiscountPct = isReseller ? Number(selectedPartner?.wholesale_discount_pct) || 0 : 0;
  const resellerUnitPrice = (msrp: number) => Math.round(msrp * (1 - resellerDiscountPct / 100));
  // Same "amount above this auto portion is what staff typed in" pattern as
  // autoBundleDiscount — the gap between a line's own MSRP and its
  // discounted price, summed, so it doesn't get double-counted on submit.
  const autoResellerDiscount =
    form.is_gift || resellerDiscountPct <= 0
      ? 0
      : lines.reduce((sum, l) => {
          const qty = Number(l.qty) || 0;
          const ownMsrp = products.find((p) => p.sku === l.sku)?.msrp_bdt ?? 0;
          return sum + (ownMsrp - resellerUnitPrice(ownMsrp)) * qty;
        }, 0);

  // Auto-fills the Discount field with the bundle savings the moment a
  // combo line is added, but backs off the instant staff types their own
  // number in — same manual-override pattern as shippingManuallySet below,
  // so a staff-entered "extra discount" (e.g. ৳600 instead of ৳500) never
  // gets silently clobbered back down to the auto amount.
  const [discountManuallySet, setDiscountManuallySet] = useState(false);
  useEffect(() => {
    if (discountManuallySet) return;
    const autoTotal = autoBundleDiscount + autoResellerDiscount;
    setForm((f) => ({ ...f, discount_bdt: autoTotal ? String(autoTotal) : '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBundleDiscount, autoResellerDiscount, discountManuallySet]);

  // Same free-shipping rule as the public checkout: once the subtotal
  // crosses the threshold, delivery is free. Auto-applied whenever the zone
  // changes or the subtotal crosses the line — but backs off the moment
  // staff types their own number into the field (a manual override, e.g. a
  // remote-area surcharge, should never get silently clobbered).
  const zoneRate = (zone: 'dhaka' | 'outside') => (orderSubtotal >= shippingRates.freeThreshold ? 0 : shippingRates[zone]);
  const [shippingManuallySet, setShippingManuallySet] = useState(false);
  useEffect(() => {
    if (fulfillment !== 'delivery' || shippingManuallySet) return;
    setForm((f) => ({ ...f, shipping_bdt: String(zoneRate(deliveryZone)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillment, deliveryZone, orderSubtotal, shippingRates, shippingManuallySet]);

  const setFulfillmentMode = (mode: 'delivery' | 'instore') => {
    setFulfillment(mode);
    setForm((f) => ({ ...f, payment_method: mode === 'delivery' ? 'cod' : 'cash' }));
  };

  const setDeliveryZoneAndRate = (zone: 'dhaka' | 'outside') => {
    setDeliveryZone(zone);
    setShippingManuallySet(false);
  };

  const orderDiscount = Number(form.discount_bdt) || 0;
  const orderShipping = fulfillment === 'delivery' ? Number(form.shipping_bdt) || 0 : 0;

  // Free when the order includes the 3-step combo (server-verified in
  // core_create_order, not trusted from here) — same rule as public
  // checkout. Not a choice in that case, so no checkbox to turn it off.
  // Which sets include a free box is set per product in ERP (gift_box_free).
  const isGiftBoxSet = (sku: string) => !!products.find((p) => p.sku === sku)?.gift_box_free;
  const hasCombo = lines.some((l) => isGiftBoxSet(l.sku));
  // Gift Box can be turned off entirely from ERP (Inventory > Products >
  // GIFT-BOX > Status = Inactive) — giftBoxPrice comes back null in that
  // case. Also requires actual stock on hand: core_create_order silently
  // drops the free combo gift box when out of stock rather than blocking
  // the sale, but the UI shouldn't claim it'll be included when it won't.
  const giftBoxAvailable = giftBoxPrice != null && (stockBySku['GIFT-BOX'] ?? 0) > 0;
  // One combo set = one free box, driven by the combo's own set quantity.
  // Outside a combo, staff pick however many boxes they want manually — box
  // scanning below is capped at whichever number applies, never unbounded.
  const giftBoxNeeded = hasCombo
    ? lines.filter((l) => isGiftBoxSet(l.sku)).reduce((sum, l) => sum + (Number(l.qty) || 0), 0)
    : giftBoxAvailable
      ? Number(giftBoxQty) || 0
      : 0;
  const giftBoxCharge = !hasCombo && !(isPriv && giftBoxFree) ? giftBoxNeeded * (giftBoxPrice ?? 0) : 0;

  const orderTotal = Math.max(0, orderSubtotal - orderDiscount + orderShipping + giftBoxCharge);

  // Mirrors core_create_order's consignment logic server-side: first-ever
  // reseller order is 100% credit, every order after requires the
  // partner's configured advance % upfront — shown here so staff know the
  // real due-now/on-credit split before submitting, not just after.
  const isFirstResellerOrder = priorOrderCount === 0;
  const advancePct = isFirstResellerOrder ? (selectedPartner?.first_order_advance_pct ?? 0) : (selectedPartner?.advance_payment_pct ?? 0);
  // The reseller's real, discounted subtotal — advance/credit is what they
  // actually owe on this order, not a percentage of the gross MSRP total.
  const actualSubtotal = Math.max(0, orderSubtotal - autoBundleDiscount - autoResellerDiscount);
  const advanceDueNow = Math.round((actualSubtotal * advancePct) / 100);
  const onCreditAmount = actualSubtotal - advanceDueNow;

  const resetForm = () => {
    setFulfillment('delivery');
    setDeliveryZone('dhaka');
    setShippingManuallySet(false);
    setDiscountManuallySet(false);
    setDiscountPct('');
    setIsReseller(defaultReseller);
    setPartnerId('');
    setForm(EMPTY_FORM);
    setLines([{ sku: '', qty: '1', scannedCodes: [] }]);
    setPhoneMatched(null);
    setSerialDrafts({});
    setGiftBoxQty('0');
    setGiftBoxFree(false);
    setGiftBoxSerials([]);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    const items = lines
      .filter((l) => l.sku && Number(l.qty) > 0)
      .map((l) => {
        const msrp = products.find((p) => p.sku === l.sku)?.msrp_bdt ?? 0;
        return resellerDiscountPct > 0
          ? { sku: l.sku, qty: Number(l.qty), unit_price_bdt: resellerUnitPrice(msrp) }
          : { sku: l.sku, qty: Number(l.qty) };
      });
    if (items.length === 0) {
      push('error', 'Add at least one line item');
      return;
    }
    if (form.is_gift && !form.gift_reason.trim()) {
      push('error', 'Add a reason for the gift order');
      return;
    }
    if (isReseller && !partnerId) {
      push('error', 'Select a reseller / partner');
      return;
    }
    const qtyBySku: Record<string, number> = {};
    for (const item of items) qtyBySku[item.sku] = (qtyBySku[item.sku] ?? 0) + item.qty;
    for (const [sku, qty] of Object.entries(qtyBySku)) {
      const available = stockBySku[sku] ?? 0;
      if (qty > available) {
        const name = products.find((p) => p.sku === sku)?.name_en || sku;
        push('error', `${name}: only ${available} in stock, but ${qty} requested`);
        return;
      }
    }
    // Pushed to `items` after the stock-check loop above runs, so it isn't
    // covered by that loop even though stockBySku now does have a real
    // GIFT-BOX figure — server-side core_create_order still rejects it if
    // actually out of stock. Price is never sent — the
    // server decides free-with-combo vs. its own MSRP — unless a privileged
    // staffer waived it manually, in which case 0 is sent as an explicit
    // override (core_create_order only honors unit_price_bdt for admin/
    // admin_low, so this silently has no effect for bd_staff either way).
    if (giftBoxNeeded > 0) {
      items.push(
        !hasCombo && isPriv && giftBoxFree
          ? { sku: 'GIFT-BOX', qty: giftBoxNeeded, unit_price_bdt: 0 }
          : { sku: 'GIFT-BOX', qty: giftBoxNeeded },
      );
    }
    if (!form.is_gift && !isReseller && !form.came_from) {
      push('error', 'Choose where this order came from');
      return;
    }
    setCreateBusy(true);
    const createOrder = (allowDuplicate: boolean) => supabase.rpc('core_create_order', {
      p_order: {
        channel: isReseller && partnerId ? 'pharmacy_b2b' : 'rxk_web',
        partner_id: isReseller ? partnerId || undefined : undefined,
        payment_method: form.payment_method,
        source: 'manual',
        fulfillment,
        customer_info: { name: form.customer_name, phone: form.customer_phone, address: form.customer_address },
        // core_create_order now applies the bundle-savings portion
        // automatically — only the amount staff entered ABOVE that auto
        // amount should be sent, otherwise the bundle discount would be
        // double-counted (once here, once server-side).
        discount_bdt: Math.max(0, (Number(form.discount_bdt) || 0) - autoBundleDiscount - autoResellerDiscount),
        shipping_bdt: fulfillment === 'delivery' ? Number(form.shipping_bdt) || 0 : 0,
        is_gift: form.is_gift,
        gift_reason: form.is_gift ? form.gift_reason.trim() : null,
        attribution: orderSourceAttribution(form.came_from),
        allow_duplicate: allowDuplicate || undefined,
      },
      p_items: items,
    });
    let { data: orderId, error } = await createOrder(false);
    // The server refuses a partner order identical to one of theirs from
    // the last 7 days (usually entered twice by mistake) until confirmed.
    const duplicateOf = error?.message.match(/DUPLICATE_ORDER:([A-Z]+-\d+-\d+)/)?.[1];
    if (duplicateOf) {
      const proceed = window.confirm(
        `${duplicateOf} already has exactly these items for this partner in the last 7 days.\n\n` +
          'If that order is being returned or corrected, open it and use Return / Edit instead.\n\nCreate a second order anyway?',
      );
      if (!proceed) {
        setCreateBusy(false);
        return;
      }
      ({ data: orderId, error } = await createOrder(true));
    }
    if (error) {
      setCreateBusy(false);
      push('error', error.message);
      return;
    }
    // Lines built from scans carry the exact physical units that went into
    // them — mark those specific codes sold + linked to this order now
    // that it exists, so stock isn't just deducted by SKU/qty but tied to
    // real serials. Best-effort: the order itself is already correctly
    // created and stock-deducted at this point either way.
    const scannedLinks = [
      ...lines.flatMap((l) => l.scannedCodes.map((code) => ({ code, sku: l.sku }))),
      ...(giftBoxNeeded > 0 ? giftBoxSerials.map((code) => ({ code, sku: 'GIFT-BOX' })) : []),
    ];
    if (orderId && scannedLinks.length > 0) {
      let linkFailures = 0;
      for (const { code } of scannedLinks) {
        const { error: linkErr } = await supabase.rpc('core_scan_link_order_unit', { p_inventory_code: code, p_order_id: orderId });
        if (linkErr) linkFailures++;
      }
      if (linkFailures > 0) {
        push('error', `Order created, but ${linkFailures} scanned unit(s) couldn't be linked for traceability`);
      }
    }
    // Walk-in sales are paid and complete at the counter — mark them
    // confirmed right away instead of leaving them in the "placed" queue.
    if (fulfillment === 'instore' && orderId) {
      const { error: advanceErr } = await supabase.rpc('core_advance_order', { p_order_id: orderId, p_next: 'confirmed' });
      if (advanceErr) {
        push('error', `Order created, but couldn't mark it confirmed: ${advanceErr.message}`);
      } else {
        const { data: createdOrder } = await supabase.from('core_orders').select('order_no').eq('id', orderId).maybeSingle();
        const orderNo = createdOrder?.order_no ?? '';
        const recipient = { phone: form.customer_phone, name: form.customer_name };
        supabase.functions
          .invoke('core-send-notification', {
            body: { action: 'send_event', event_key: 'order_confirmed', recipient, variables: { order_no: orderNo } },
          })
          .catch(() => {});
        supabase.rpc('core_generate_invoice_for_order', { p_order_id: orderId }).then(({ data: invoiceId, error: invErr }) => {
          if (invErr || !invoiceId) return;
          supabase.functions.invoke('send-invoice', { body: { action: 'send_email', request_id: invoiceId } }).catch(() => {});
          supabase.functions
            .invoke('core-send-notification', {
              body: { action: 'send_event', event_key: 'invoice_issued', recipient, variables: { order_no: orderNo } },
            })
            .catch(() => {});
        });
      }
    }
    setCreateBusy(false);
    push('success', form.is_gift ? 'Gift order created' : 'Order created');
    onClose();
    resetForm();
    onCreated();
  };

  const isGiftBoxPicker = pickerLineIdx === 'giftbox';
  const pickerLine = typeof pickerLineIdx === 'number' ? lines[pickerLineIdx] : null;
  const pickerEffectiveSku = isGiftBoxPicker ? 'GIFT-BOX' : pickerComponentSku || pickerLine?.sku || '';
  const pickerProduct = pickerEffectiveSku ? products.find((p) => p.sku === pickerEffectiveSku) : null;
  const pickerSkuPrefix = (pickerProduct?.serial_prefix ?? '').toUpperCase();
  const pickerSearchTrimmed = pickerSearch.trim().toUpperCase();
  // Empty search: browse the capped prefetch (fast, no round-trip).
  // Non-empty search: pickerLiveMatches (from the effect below) — a live
  // query against the real table, not limited to the first 300 codes.
  const pickerCandidates = pickerSearchTrimmed
    ? (pickerLiveMatches ?? [])
    : isGiftBoxPicker
      ? giftBoxCodes
      : availableCodes.filter((c) => !pickerEffectiveSku || c.startsWith(pickerSkuPrefix));
  const pickerOptions = isGiftBoxPicker
    ? pickerCandidates.filter((c) => !giftBoxSerials.includes(c))
    : pickerLine
      ? pickerCandidates.filter((c) => !pickerLine.scannedCodes.includes(c))
      : [];
  // The default (no-search) list is deliberately capped when fetched
  // (availableCodes / giftBoxCodes both .limit(300) per SKU — see the fetch
  // above) so the modal stays responsive even for a SKU with thousands of
  // codes. That cap is NOT the real stock count, so the caption below must
  // read from stockBySku (the live core_stock_current_v total) instead of
  // pickerOptions.length, or it lies about how much is actually in stock
  // once true stock exceeds the fetch cap.
  const pickerRealStock = Math.max(pickerOptions.length, stockBySku[pickerEffectiveSku] ?? 0);

  // Debounced live search — runs an actual DB query scoped to the current
  // SKU whenever the search box has text, instead of just filtering
  // whatever happened to be in the capped prefetch.
  useEffect(() => {
    if (pickerLineIdx === null || !pickerSearchTrimmed) {
      setPickerLiveMatches(null);
      return;
    }
    if (!pickerEffectiveSku) {
      setPickerLiveMatches([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('core_product_authenticity_codes')
        .select('inventory_code')
        .eq('sku', pickerEffectiveSku)
        .eq('status', 'assigned')
        .ilike('inventory_code', `%${pickerSearchTrimmed}%`)
        .order('inventory_code')
        .limit(60);
      if (!cancelled) setPickerLiveMatches(((data as { inventory_code: string }[] | null) ?? []).map((r) => r.inventory_code));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [pickerLineIdx, pickerEffectiveSku, pickerSearchTrimmed]);
  const getDraft = (key: string) => serialDrafts[key] ?? '';
  const setDraft = (key: string, v: string) => setSerialDrafts((d) => ({ ...d, [key]: v }));

  return (
    <>
    <Modal open={open} onClose={onClose} title="New Order" icon={<ShoppingCart className="w-4 h-4" />} busy={createBusy} maxWidth="max-w-7xl" closeOnEscape={false}>
      <form onSubmit={handleCreate} className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-3.5">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFulfillmentMode('delivery')}
              className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-[12.5px] font-semibold cursor-pointer transition-colors ${
                fulfillment === 'delivery' ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-border text-ink-faint hover:border-primary-200'
              }`}
            >
              <Truck className="w-4 h-4" /> Courier Delivery
            </button>
            <button
              type="button"
              onClick={() => setFulfillmentMode('instore')}
              className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-[12.5px] font-semibold cursor-pointer transition-colors ${
                fulfillment === 'instore' ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-border text-ink-faint hover:border-primary-200'
              }`}
            >
              <Store className="w-4 h-4" /> Walk-in (In-store)
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <Field label={form.is_gift ? 'Phone (optional)' : 'Phone'}>
              <div className="relative">
                <Input
                  required={!form.is_gift}
                  value={form.customer_phone}
                  onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
                  className={phoneLookupBusy || phoneMatched ? '!pr-9' : ''}
                />
                {phoneLookupBusy ? (
                  <Loader2 className="w-3.5 h-3.5 text-ink-faint absolute right-3 top-1/2 -translate-y-1/2 animate-spin pointer-events-none" />
                ) : (
                  phoneMatched && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-success absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  )
                )}
              </div>
              {phoneMatched === 'account' && <p className="text-[10.5px] text-success mt-1">Matched a registered account — details filled in</p>}
              {phoneMatched === 'previous' && <p className="text-[10.5px] text-success mt-1">Matched a previous order — details filled in</p>}
            </Field>
            <Field label="Customer Name">
              <Input required value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} />
            </Field>
          </div>
          {fulfillment === 'delivery' && (
            <Field label="Delivery Area">
              <Select value={deliveryZone} onChange={(e) => setDeliveryZoneAndRate(e.target.value as 'dhaka' | 'outside')}>
                <option value="dhaka">Inside Dhaka (৳{shippingRates.dhaka})</option>
                <option value="outside">Outside Dhaka (৳{shippingRates.outside})</option>
              </Select>
            </Field>
          )}
          <Field label={fulfillment === 'delivery' ? 'Address' : 'Address (optional)'}>
            <Input
              required={fulfillment === 'delivery'}
              value={form.customer_address}
              onChange={(e) => setForm((f) => ({ ...f, customer_address: e.target.value }))}
            />
          </Field>

          <div className="space-y-2 pt-1 border-t border-border-subtle">
            <div className="flex items-center justify-between pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Line Items</p>
              <span className="flex items-center gap-1 text-[10.5px] text-ink-faint">
                {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <ScanLine className="w-3 h-3" />}
                {scanning ? 'Looking up scan…' : 'Scan a barcode to add'}
              </span>
            </div>
            {/* No overflow-hidden here — the Serial field's suggestion
                dropdown is position:absolute and would get clipped by any
                overflow-hidden ancestor even though it escapes normal flow.
                rounded-t-xl on the header row keeps its square corners from
                poking past the container's rounded top instead. */}
            <div className="rounded-xl border border-border-subtle">
              <div className="grid gap-2 px-2.5 py-2 bg-surface-muted rounded-t-xl" style={{ gridTemplateColumns: '1.1fr 1.3fr 60px 28px' }}>
                <span className="text-[9.5px] font-semibold uppercase tracking-wide text-ink-faint">Product</span>
                <span className="text-[9.5px] font-semibold uppercase tracking-wide text-ink-faint">Serial</span>
                <span className="text-[9.5px] font-semibold uppercase tracking-wide text-ink-faint">Qty</span>
                <span />
              </div>
              {lines.map((line, idx) => {
                const bundleParts = bundleComponents[line.sku];
                const isBundle = !!bundleParts && bundleParts.length > 0;
                const setsWanted = Number(line.qty) || 0;
                return (
                  <div
                    key={idx}
                    className="grid gap-2 px-2.5 py-2 items-start border-t border-border-subtle"
                    style={{ gridTemplateColumns: '1.1fr 1.3fr 60px 28px' }}
                  >
                    <Select required disabled={line.scannedCodes.length > 0} value={line.sku} onChange={(e) => updateLine(idx, { sku: e.target.value })}>
                      <option value="">Select product…</option>
                      {products.map((p) => {
                        const stock = stockBySku[p.sku] ?? 0;
                        return (
                          <option key={p.sku} value={p.sku} disabled={stock <= 0}>
                            {p.name_en || p.sku} — {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                          </option>
                        );
                      })}
                    </Select>

                    {/* Per-line serial tracking. A combo/set SKU has no
                        codes of its own — it's assembled from components —
                        so it gets one mini section per component instead
                        of a single generic field. */}
                    <div>
                      {isBundle ? (
                        <div className="space-y-2">
                          {bundleParts!.map((part) => {
                            const partProduct = products.find((p) => p.sku === part.sku);
                            const partPrefix = (partProduct?.serial_prefix ?? '').toUpperCase();
                            const partCodes = line.scannedCodes.filter((c) => c.startsWith(partPrefix));
                            const needed = setsWanted * part.qty_per_bundle;
                            const partSuggestions = availableCodes.filter((c) => c.startsWith(partPrefix) && !line.scannedCodes.includes(c));
                            const draftKey = `${idx}:${part.sku}`;
                            return (
                              <div key={part.sku}>
                                <p className="text-[9px] font-semibold text-ink-faint">
                                  {partProduct?.name_en || part.sku} <span className="font-normal text-ink-disabled">({partCodes.length}/{needed})</span>
                                </p>
                                {partCodes.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-1 mt-0.5">
                                    {partCodes.map((code) => (
                                      <span key={code} className="inline-flex items-center gap-1 rounded-md bg-success-bg text-success text-[10px] font-mono pl-1.5 pr-1 py-0.5">
                                        {code}
                                        <button
                                          type="button"
                                          onClick={() => updateLine(idx, { scannedCodes: line.scannedCodes.filter((c) => c !== code) })}
                                          className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-success/20 cursor-pointer"
                                          aria-label={`Remove ${code}`}
                                        >
                                          <X className="w-2.5 h-2.5" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {partCodes.length < needed && (
                                <div className="flex gap-1.5 mt-0.5">
                                  <SerialTypeahead
                                    value={getDraft(draftKey)}
                                    onChange={(v) => setDraft(draftKey, v)}
                                    onSubmit={(code) => {
                                      addSerialToLine(idx, code);
                                      setDraft(draftKey, '');
                                    }}
                                    suggestions={partSuggestions}
                                    liveSearch={async (q) => (await searchSerials(part.sku, q)).filter((c) => !line.scannedCodes.includes(c))}
                                    placeholder="Scan, paste, or browse…"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPickerComponentSku(part.sku);
                                      setPickerSearch('');
                                      setPickerLineIdx(idx);
                                    }}
                                    className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg border border-border text-ink-faint hover:border-primary-300 hover:text-primary-800 cursor-pointer"
                                    aria-label={`Browse ${partProduct?.name_en || part.sku} serials`}
                                  >
                                    <ListChecks className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                )}
                              </div>
                            );
                          })}
                          {(() => {
                            const bundleProduct = products.find((p) => p.sku === line.sku);
                            const componentsTotal = bundleParts!.reduce(
                              (sum, part) => sum + (products.find((p) => p.sku === part.sku)?.msrp_bdt ?? 0) * part.qty_per_bundle * setsWanted,
                              0,
                            );
                            const savings = componentsTotal - (bundleProduct?.msrp_bdt ?? 0) * setsWanted;
                            if (savings <= 0) return null;
                            return (
                              <p className="text-[9.5px] text-ink-faint pt-0.5">
                                ৳{componentsTotal.toLocaleString()} value — <span className="font-semibold text-primary-800">Bundle Savings ৳{savings.toLocaleString()}</span>
                              </p>
                            );
                          })()}
                        </div>
                      ) : (
                        (() => {
                          const skuPrefix = (products.find((p) => p.sku === line.sku)?.serial_prefix ?? '').toUpperCase();
                          const suggestions = (line.sku ? availableCodes.filter((c) => c.startsWith(skuPrefix)) : availableCodes).filter(
                            (c) => !line.scannedCodes.includes(c),
                          );
                          const draftKey = String(idx);
                          return (
                            <>
                              {line.scannedCodes.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {line.scannedCodes.map((code) => (
                                    <span key={code} className="inline-flex items-center gap-1 rounded-md bg-success-bg text-success text-[10px] font-mono pl-1.5 pr-1 py-0.5">
                                      {code}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const next = line.scannedCodes.filter((c) => c !== code);
                                          updateLine(idx, { scannedCodes: next, qty: next.length > 0 ? String(next.length) : line.qty });
                                        }}
                                        className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-success/20 cursor-pointer"
                                        aria-label={`Remove ${code}`}
                                      >
                                        <X className="w-2.5 h-2.5" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="flex gap-1.5">
                                <SerialTypeahead
                                  value={getDraft(draftKey)}
                                  onChange={(v) => setDraft(draftKey, v)}
                                  onSubmit={(code) => {
                                    addSerialToLine(idx, code);
                                    setDraft(draftKey, '');
                                  }}
                                  suggestions={suggestions}
                                  liveSearch={line.sku ? async (q) => (await searchSerials(line.sku, q)).filter((c) => !line.scannedCodes.includes(c)) : undefined}
                                  placeholder={line.sku ? 'Scan, paste, or browse…' : 'Scan or pick a product first'}
                                  disabled={!line.sku}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPickerComponentSku(null);
                                    setPickerSearch('');
                                    setPickerLineIdx(idx);
                                  }}
                                  disabled={!line.sku}
                                  className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg border border-border text-ink-faint hover:border-primary-300 hover:text-primary-800 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                  aria-label="Browse in-stock serials"
                                >
                                  <ListChecks className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </>
                          );
                        })()
                      )}
                    </div>

                    <Input
                      type="number"
                      min={1}
                      required
                      disabled={!isBundle && line.scannedCodes.length > 0}
                      value={line.qty}
                      onChange={(e) => updateLine(idx, { qty: e.target.value })}
                    />

                    {lines.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-ink-faint hover:bg-error-bg hover:text-error cursor-pointer flex-shrink-0"
                        aria-label="Remove line"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                );
              })}
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={addLine}>
              <Plus className="w-3.5 h-3.5" /> Add Line
            </Button>
          </div>
        </div>

        <div className="space-y-3.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Order Summary</p>
          <div className="rounded-xl border border-border-subtle overflow-hidden">
            <div className="p-3.5 space-y-2.5 bg-surface-muted">
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-ink-faint">Subtotal</span>
                <span className="text-[12.5px] font-semibold text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  ৳{orderSubtotal.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <label className="text-[12px] text-ink-faint flex-shrink-0">Discount</label>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={discountPct}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDiscountPct(value);
                      setDiscountManuallySet(true);
                      const pct = Number(value);
                      if (value && Number.isFinite(pct) && pct >= 0) {
                        setForm((f) => ({ ...f, discount_bdt: String(Math.round((orderSubtotal * pct) / 100)) }));
                      }
                    }}
                    placeholder="%"
                    className="!w-14 !py-1 text-right"
                  />
                  <span className="text-[11px] text-ink-faint flex-shrink-0">%</span>
                  <Input
                    type="number"
                    min={0}
                    value={form.discount_bdt}
                    onChange={(e) => {
                      setDiscountPct('');
                      setDiscountManuallySet(true);
                      setForm((f) => ({ ...f, discount_bdt: e.target.value }));
                    }}
                    placeholder="0"
                    className="!w-24 !py-1 text-right"
                  />
                  <span className="text-[11px] text-ink-faint flex-shrink-0">৳</span>
                </div>
              </div>
              {fulfillment === 'delivery' && (
                <div className="flex items-center justify-between gap-2">
                  <label className="text-[12px] text-ink-faint flex-shrink-0">
                    Delivery Charge (৳){orderSubtotal >= shippingRates.freeThreshold && !shippingManuallySet && (
                      <span className="block text-[10px] text-success font-semibold">Free — order over ৳{shippingRates.freeThreshold.toLocaleString()}</span>
                    )}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={form.shipping_bdt}
                    onChange={(e) => {
                      setShippingManuallySet(true);
                      setForm((f) => ({ ...f, shipping_bdt: e.target.value }));
                    }}
                    placeholder="0"
                    className="!w-28 !py-1 text-right"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between px-3.5 py-3 bg-gradient-to-b from-primary-700 to-primary-800">
              <span className="text-[11px] font-bold uppercase tracking-wide text-white/80">Total</span>
              <span className="text-white font-extrabold text-[19px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                ৳{orderTotal.toLocaleString()}
              </span>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer w-fit select-none">
            <input
              type="checkbox"
              checked={isReseller}
              onChange={(e) => {
                setIsReseller(e.target.checked);
                if (!e.target.checked) setPartnerId('');
              }}
              className="w-4 h-4 accent-primary-800 cursor-pointer"
            />
            <span className="text-[12.5px] font-semibold text-ink">🏢 This is a reseller / partner order</span>
          </label>

          {isReseller && (
            <Field label="Reseller / Partner">
              <SearchableSelect
                placeholder="Select reseller…"
                value={partnerId}
                items={partners
                  .filter((p) => p.status === 'active' && p.type !== 'courier')
                  .map((p) => ({ id: p.id, name: `${p.name} (${p.type})` }))}
                onChange={(id) => {
                  setPartnerId(id);
                  const partner = partners.find((p) => p.id === id);
                  if (partner) {
                    setForm((f) => ({
                      ...f,
                      customer_name: partner.name,
                      customer_phone: partner.phone || f.customer_phone,
                      customer_address: partner.address || f.customer_address,
                    }));
                  }
                }}
              />
            </Field>
          )}

          {isReseller && partnerId && (
            <div className="rounded-xl border border-primary-200 bg-primary-50 p-3 space-y-1.5">
              {priorOrderCount === null ? (
                <p className="text-[11.5px] text-ink-faint">Checking order history…</p>
              ) : orderSubtotal === 0 ? (
                <p className="text-[11.5px] text-ink-faint">Add products to see the due-now / on-credit split.</p>
              ) : isFirstResellerOrder && advancePct === 0 ? (
                <p className="text-[12px] font-semibold text-primary-800">
                  🎁 First order for this reseller — ships 100% on credit (no payment now, they pay once resold).
                </p>
              ) : (
                <>
                  {isFirstResellerOrder && (
                    <p className="text-[11px] font-semibold text-primary-700 mb-0.5">First order for this reseller</p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[11.5px] text-ink-faint">Due now ({advancePct}% advance)</span>
                    <span className="text-[12.5px] font-bold text-primary-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      ৳{advanceDueNow.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11.5px] text-ink-faint">On credit (paid once resold)</span>
                    <span className="text-[12.5px] font-bold text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      ৳{onCreditAmount.toLocaleString()}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer w-fit select-none">
            <input
              type="checkbox"
              checked={form.is_gift}
              onChange={(e) => setForm((f) => ({ ...f, is_gift: e.target.checked }))}
              className="w-4 h-4 accent-primary-800 cursor-pointer"
            />
            <span className="text-[12.5px] font-semibold text-ink">🎁 This is a free / gift order</span>
          </label>

          {form.is_gift ? (
            <Field label="Reason / Purpose">
              <Textarea
                required
                rows={2}
                value={form.gift_reason}
                onChange={(e) => setForm((f) => ({ ...f, gift_reason: e.target.value }))}
                placeholder="e.g. Influencer sample, customer compensation, event giveaway…"
              />
            </Field>
          ) : (
            <Field label="Payment Method">
              <Select value={form.payment_method} onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}>
                {fulfillment === 'delivery' ? (
                  <>
                    <option value="cod">Cash on Delivery</option>
                    <option value="prepaid">Prepaid / No COD</option>
                  </>
                ) : (
                  <>
                    <option value="cash">Cash</option>
                    <option value="pos_card">Card (POS)</option>
                    <option value="prepaid">Prepaid / No COD</option>
                  </>
                )}
                {paymentGateways.map((g) => (
                  <option key={g.gateway_key} value={g.gateway_key}>
                    {g.display_name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {!isReseller && (
            <Field label={form.is_gift ? 'Came from (optional)' : 'Came from'}>
              <Select value={form.came_from} onChange={(e) => setForm((f) => ({ ...f, came_from: e.target.value }))}>
                <option value="">— Choose —</option>
                {ORDER_SOURCES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {giftBoxAvailable && (
            hasCombo ? (
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] font-semibold text-ink">🎁 Gift Box included (combo order — free)</span>
              </div>
            ) : (
              <Field label="🎁 Add a Gift Box — how many?">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    value={giftBoxQty}
                    onChange={(e) => setGiftBoxQty(e.target.value)}
                    className="w-20"
                  />
                  {giftBoxNeeded > 0 && (
                    <span className="text-[12.5px] font-bold text-ink">
                      {isPriv && giftBoxFree ? 'Free' : `+৳${giftBoxCharge.toLocaleString()}`}
                    </span>
                  )}
                  {isPriv && giftBoxNeeded > 0 && (
                    <label className="flex items-center gap-1.5 cursor-pointer select-none ml-1">
                      <input
                        type="checkbox"
                        checked={giftBoxFree}
                        onChange={(e) => setGiftBoxFree(e.target.checked)}
                        className="w-3.5 h-3.5 accent-primary-800 cursor-pointer"
                      />
                      <span className="text-[11.5px] text-ink-faint">Waive charge (free)</span>
                    </label>
                  )}
                </div>
              </Field>
            )
          )}

          {giftBoxNeeded > 0 && (
            <Field label={`Box Serial (optional — scan/select the physical box${giftBoxNeeded === 1 ? '' : 'es'} used) (${giftBoxSerials.length}/${giftBoxNeeded})`}>
              {giftBoxSerials.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {giftBoxSerials.map((code) => (
                    <span key={code} className="inline-flex items-center gap-1 rounded-md bg-success-bg text-success text-[10px] font-mono pl-1.5 pr-1 py-0.5">
                      {code}
                      <button
                        type="button"
                        onClick={() => setGiftBoxSerials((prev) => prev.filter((c) => c !== code))}
                        className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-success/20 cursor-pointer"
                        aria-label={`Remove ${code}`}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {giftBoxSerials.length < giftBoxNeeded && (
                <div className="flex gap-1.5">
                  <SerialTypeahead
                    value={getDraft('giftbox')}
                    onChange={(v) => setDraft('giftbox', v)}
                    onSubmit={(code) => {
                      addGiftBoxSerial(code);
                      setDraft('giftbox', '');
                    }}
                    suggestions={giftBoxCodes}
                    liveSearch={async (q) => (await searchSerials('GIFT-BOX', q)).filter((c) => !giftBoxSerials.includes(c))}
                    placeholder="Scan/type box barcode…"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPickerSearch('');
                      setPickerLineIdx('giftbox');
                    }}
                    className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg border border-border text-ink-faint hover:border-primary-300 hover:text-primary-800 cursor-pointer"
                    aria-label="Browse Gift Box serials"
                  >
                    <ListChecks className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </Field>
          )}

          <Button type="submit" loading={createBusy} className="w-full">Create Order</Button>
        </div>
      </form>
    </Modal>

    <Modal
      open={pickerLineIdx !== null}
      onClose={() => {
        setPickerLineIdx(null);
        setPickerComponentSku(null);
      }}
      title="Select a Serial"
      subtitle={isGiftBoxPicker ? 'Gift Box' : (pickerProduct?.name_en ?? pickerLine?.sku ?? undefined)}
      icon={<ListChecks className="w-4 h-4" />}
      maxWidth="max-w-2xl"
    >
      <div className="relative">
        <Search className="w-4 h-4 text-ink-faint absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          autoFocus
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
          placeholder="Search serial…"
          className="!pl-9 font-mono"
        />
      </div>
      {pickerOptions.length === 0 ? (
        <p className="text-[12.5px] text-ink-faint text-center py-10">No matching in-stock serials</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 overflow-y-auto pr-1" style={{ maxHeight: '260px' }}>
          {pickerOptions.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => {
                if (isGiftBoxPicker) {
                  addGiftBoxSerial(code);
                } else if (typeof pickerLineIdx === 'number') {
                  addSerialToLine(pickerLineIdx, code);
                }
                setPickerSearch('');
                setPickerLineIdx(null);
                setPickerComponentSku(null);
              }}
              className="rounded-lg border border-border-subtle px-2.5 py-2 text-[11.5px] font-mono text-ink text-left hover:border-primary-300 hover:bg-primary-50 cursor-pointer transition-colors"
            >
              {code}
            </button>
          ))}
        </div>
      )}
      <p className="text-[10.5px] text-ink-faint">
        Showing {pickerOptions.length} of {pickerRealStock} in-stock serial{pickerRealStock === 1 ? '' : 's'}
        {pickerRealStock > pickerOptions.length ? ' — search to narrow' : ''}
      </p>
    </Modal>
    </>
  );
}
